import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { finalize, switchMap, tap, map, filter, first } from 'rxjs/operators';
import { timer, throwError, of, EMPTY, Observable } from 'rxjs';
import { ImportTechStackDto } from 'src/portal/admin-buyerscaddy-api/models/import-tech-stack-dto';
import { ImportTechStackStatusDto } from 'src/portal/admin-buyerscaddy-api/models/import-tech-stack-status-dto';
import { ImportTechStackResultDto } from 'src/portal/admin-buyerscaddy-api/models/import-tech-stack-result-dto';
import { TechStackService } from 'src/portal/admin-buyerscaddy-api/services/tech-stack.service';

export interface TechstacksImportDialogData {
  techStacks?: TechstacksImportSourceRow[];
  title?: string;
}

export interface TechstacksImportSourceRow {
  Company?: string;
  CompanyDomain?: string;
  Product?: string;
  Vendor?: string;
  VendorDomain?: string;
  HitCount?: number | string;
  Country?: string;
  Scope?: string;
  Source?: string;
  Probability?: string;
  DateFirstVerified?: string;
  DateLastVerified?: string;
}

interface TechstacksImportDisplayRow {
  dto: ImportTechStackDto;
  selected: boolean;
  country?: string;
  scope?: string;
  source?: string;
  probability?: string;
  intensity: number;
}

@Component({
  selector: 'techstacks-import',
  templateUrl: './techstacks-import.component.html',
  styleUrls: ['./techstacks-import.component.scss'],
})
export class TechstacksImportComponent implements OnInit {
  readonly displayedColumns: string[] = [
    'selected',
    'company',
    'companyDomain',
    'product',
    'vendor',
    'vendorDomain',
    'source',
    'probability',
    'dateFirstVerified',
    'dateLastVerified',
  ];
  readonly resultColumns: string[] = [
    'status',
    'company',
    'companyDomain',
    'product',
    'vendor',
    'vendorDomain',
    'dateFirstVerified',
    'dateLastVerified',
    'intensity',
    'errors',
  ];

  rows: TechstacksImportDisplayRow[] = [];
  importResults: ImportTechStackResultDto[] = [];
  isLoading = false;
  hasImported = false;
  importErrorMessage: string | null = null;
  progressMessage: string | null = null;
  progressValue: number = 0;

  constructor(
    private readonly dialogRef: MatDialogRef<
      TechstacksImportComponent,
      ImportTechStackResultDto[] | undefined
    >,
    @Inject(MAT_DIALOG_DATA)
    private readonly dialogData: TechstacksImportDialogData,
    private techStackService: TechStackService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    console.log('TechstacksImportComponent initialized v2');
    this.rows = (this.dialogData?.techStacks ?? []).map((row) => this.createDisplayRow(row));
  }

  get title(): string {
    return this.dialogData.title ?? 'Import Items into BuyerCaddy';
  }

  get selectedCount(): number {
    return this.rows.filter((row) => row.selected).length;
  }

  get totalCount(): number {
    return this.rows.length;
  }

  get hasRows(): boolean {
    return this.rows.length > 0;
  }

  get hasResults(): boolean {
    return this.importResults.length > 0;
  }

  get successCount(): number {
    return this.importResults.filter((result) => !this.hasErrors(result)).length;
  }

  get failureCount(): number {
    return this.importResults.filter((result) => this.hasErrors(result)).length;
  }

  get summaryMessage(): string {
    if (!this.hasResults) {
      return 'No data returned from import.';
    }

    if (this.failureCount === 0) {
      return `Imported ${this.successCount} stack${this.successCount === 1 ? '' : 's'} successfully.`;
    }

    const successPart =
      this.successCount > 0
        ? `${this.successCount} stack${this.successCount === 1 ? '' : 's'} imported`
        : 'No stacks imported';

    return `${successPart}. ${this.failureCount} stack${this.failureCount === 1 ? '' : 's'} failed.`;
  }

  toggleRowSelection(row: TechstacksImportDisplayRow, checked: boolean): void {
    row.selected = checked;
  }

  onClose(): void {
    const payload = this.hasImported ? this.importResults : undefined;
    this.dialogRef.close(payload);
  }

  onConfirm(): void {
    this.importTechStaks();
  }

  trackByCompanyAndProduct(
    index: number,
    row: TechstacksImportDisplayRow
  ): string {
    const { company, product, vendor } = row.dto;
    return `${index}::${company}::${product}::${vendor}`;
  }

  private createDisplayRow(
    row: TechstacksImportSourceRow
  ): TechstacksImportDisplayRow {
    const intensity = this.toIntensity(row.HitCount);

    return {
      dto: {
        company: this.normalizeString(row.Company),
        companyDomain: this.normalizeString(row.CompanyDomain),
        product: this.normalizeString(row.Product),
        vendor: this.normalizeString(row.Vendor),
        vendorDomain: this.normalizeString(row.VendorDomain),
        dateFirstVerified: this.normalizeDate(row.DateFirstVerified),
        dateLastVerified: this.normalizeDate(row.DateLastVerified),
        intensity,
      },
      selected: true,
      country: this.normalizeString(row.Country),
      scope: this.normalizeString(row.Scope),
      source: this.normalizeString(row.Source),
      probability: this.normalizeString(row.Probability),
      intensity,
    };
  }

  private normalizeString(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim();
  }

  private normalizeDate(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) {
      return '';
    }

    return value.trim();
  }

  private toIntensity(value: unknown): number {
    return 1;
  }

  private importTechStaks(): void {
    if (this.hasImported || this.isLoading) {
      return;
    }

    const payload = this.rows.filter((row) => row.selected).map((row) => row.dto);
    if (!payload.length) {
      return;
    }

    this.isLoading = true;
    this.importErrorMessage = null;
    this.progressMessage = 'Starting import...';
    this.progressValue = 0;
    this.cdr.detectChanges();

    this.techStackService
      .importTechStacksAsync({
        body: payload,
      })
      .pipe(
        switchMap((res) => {
          let importId = res.importId;
          if (!importId) {
            return throwError(() => new Error('Import ID not returned'));
          }
          // Trim prefix if present (e.g., "stackImport/UUID" -> "UUID")
          if (importId.includes('/')) {
            importId = importId.split('/').pop()!;
          }
          
          return this.pollImportStatus(importId).pipe(
            switchMap(() => this.techStackService.getImportTechStacksAsync({ importId }))
          );
        }),
        finalize(() => {
          this.isLoading = false;
          this.hasImported = true;
          this.progressMessage = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res) => {
          this.importResults = res ?? [];
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Failed to import tech stacks', error);
          this.importResults = [];
          this.importErrorMessage =
            'Failed to import tech stacks. Please try again later.';
          this.cdr.detectChanges();
        },
      });
  }

  private pollImportStatus(importId: string): Observable<ImportTechStackStatusDto> {
    return this.techStackService.getImportTechStacksAsyncStatus({ importId }).pipe(
      map((response: any) => Array.isArray(response) ? response[0] : response),
      switchMap((status) => {
        if (status) {
          const finished = Number(status.finished);
          const total = Number(status.total);
          const isDone = finished >= total;

          this.progressValue = total > 0 ? (finished / total) * 100 : 0;
          this.progressMessage = `Processing ${status.finished} of ${status.total} (Success: ${status.success})...`;
          this.cdr.detectChanges();

          if (isDone) {
            return of(status);
          }
        } else {
          return throwError(() => new Error('Async import status not found.'));
        }
        
        return timer(2000).pipe(
          switchMap(() => this.pollImportStatus(importId))
        );
      })
    );
  }

  private hasErrors(result: ImportTechStackResultDto): boolean {
    return !!result?.errors?.length;
  }

  getResultStatus(result: ImportTechStackResultDto): string {
    return this.hasErrors(result) ? 'Failed' : 'Success';
  }

  getResultErrors(result: ImportTechStackResultDto): string {
    return (result.errors ?? []).join('; ');
  }

  trackByResultRow(index: number, result: ImportTechStackResultDto): string {
    const stack = result.stack;
    return `${index}::${stack?.companyDomain ?? ''}::${stack?.product ?? ''}`;
  }

  get hasImportError(): boolean {
    return !!this.importErrorMessage;
  }
}
